import { createSession, deleteSession, startInteraction, pollInteraction } from './js/portal/gaia-chat';
import { get_feature_flag_property } from './js/portal/feature_flags';
import {
  createState,
  MessageBubble,
  ErrorBubble,
  TypingIndicator,
  handlePollResult,
  createChatHelpers,
} from './js/portal/rai-chat-utils';
import {
  openDB,
  createChatSession,
  updateSession,
  getSession,
  getAllSessions,
  deleteSessionById,
  addMessage,
  getSessionMessages,
} from './js/portal/rai-session-store';
import bmwLogoSrc from '../images/bmw-logo-new-white.svg';

// ---------------------------------------------------------------------------
// Date grouping helpers
// ---------------------------------------------------------------------------

function getDateGroup(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const sevenDays = new Date(today);
  sevenDays.setDate(sevenDays.getDate() - 7);

  if (d >= today) return 'Today';
  if (d >= yesterday) return 'Yesterday';
  if (d >= sevenDays) return 'Previous 7 Days';
  return 'Older';
}

function groupSessionsByDate(sessions) {
  const groups = {};
  const order = ['Today', 'Yesterday', 'Previous 7 Days', 'Older'];
  for (const s of sessions) {
    const g = getDateGroup(s.last_activity);
    if (!groups[g]) groups[g] = [];
    groups[g].push(s);
  }
  return order.filter(g => groups[g]).map(g => ({ label: g, sessions: groups[g] }));
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

export default function RaiChatPage() {
  const wrapper = <div class="rai-page" style="display:none" />;

  get_feature_flag_property('gaia-chatbot.enabled', false)
    .then(enabled => {
      if (enabled) {
        wrapper.style.display = '';
        wrapper.appendChild(buildPage());
      } else {
        window.location.hash = '#does-not-exist';
      }
    })
    .catch(() => {
      window.location.hash = '#does-not-exist';
    });

  return wrapper;
}

function buildPage() {
  // ---- State ----
  const activeSessionId = createState(null);
  const messages = createState([]);
  const chatHistory = createState([]);
  const loading = createState(false);
  const sessions = createState([]);
  const loadingSessions = new Set(); // session IDs with in-flight requests
  const unreadSessions = new Set(); // session IDs with new messages the user hasn't seen

  let abortController = null;
  let lastFailedText = null;

  // ---- DOM refs ----
  let sidebarListEl;
  let messagesEl;
  let inputEl;
  let typingEl;
  let sendBtnEl;
  let scrollPillEl;
  let emptyMainEl;

  // ---- Shared chat helpers (from rai-chat-utils) ----
  const chatRefs = { messagesEl: null, scrollPillEl: null, typingEl: null, inputEl: null, sendBtnEl: null };
  const {
    scrollToBottom,
    updateScrollPill,
    hideEmptyState,
    showEmptyState,
    appendMessage,
    appendStreamingBubble,
    showTyping,
    hideTyping,
    setInputEnabled,
  } = createChatHelpers(chatRefs);

  // ---- Helpers ----

  function hideMainEmpty() {
    if (emptyMainEl) emptyMainEl.style.display = 'none';
  }

  function showMainEmpty() {
    if (emptyMainEl) emptyMainEl.style.display = '';
  }

  // ---- Error handling ----

  function appendErrorBubble(text, showRetry = true) {
    hideEmptyState();
    const errorEl = <ErrorBubble text={text} showRetry={showRetry} />;
    messagesEl.appendChild(errorEl);
    if (showRetry) {
      const retryBtn = errorEl.querySelector('.gaia-retry-btn');
      if (retryBtn) retryBtn.addEventListener('click', () => retryLastMessage());
    }
    scrollToBottom();
    return errorEl;
  }

  function appendContentFilteredError(errorText, blockedUserText) {
    hideEmptyState();
    const displayText =
      errorText ||
      'Your message was blocked by content moderation. You can continue chatting or start a new conversation.';
    const errorEl = (
      <div class="gaia-msg gaia-msg--ai gaia-msg--error">
        <i class="gaia-msg__icon gaia-msg__icon--error fas fa-exclamation-circle" />
        <div class="gaia-msg__bubble gaia-msg__bubble--error">
          <span class="gaia-error__text">{displayText}</span>
          {blockedUserText && (
            <details class="gaia-blocked-details">
              <summary>Show blocked message</summary>
              <pre class="gaia-blocked-text">{blockedUserText}</pre>
            </details>
          )}
          <button class="gaia-retry-btn" data-tooltip="Start new conversation">
            <i class="fas fa-trash-alt" />
            <span>New Conversation</span>
          </button>
        </div>
      </div>
    );
    messagesEl.appendChild(errorEl);
    const btn = errorEl.querySelector('.gaia-retry-btn');
    if (btn) btn.addEventListener('click', () => handleNewSession());
    scrollToBottom();
  }

  function removeErrorBubbles() {
    const errorBubbles = messagesEl?.querySelectorAll('.gaia-msg--error');
    if (errorBubbles) errorBubbles.forEach(el => el.remove());
    messages.set(prev => prev.filter(m => m.role !== 'error'));
  }

  function retryLastMessage() {
    if (!lastFailedText || loading.get()) return;
    const text = lastFailedText;
    lastFailedText = null;
    removeErrorBubbles();
    const orphanStreaming = messagesEl?.querySelectorAll('.gaia-msg--ai:not(.gaia-msg--error)');
    if (orphanStreaming?.length) orphanStreaming[orphanStreaming.length - 1].remove();
    messages.set(prev => {
      const lastAiIdx = prev.findLastIndex(m => m.role === 'ai');
      if (lastAiIdx >= 0) return [...prev.slice(0, lastAiIdx), ...prev.slice(lastAiIdx + 1)];
      return prev;
    });
    sendUserMessage(text, true);
  }

  // ---- Core send ----

  async function handleSend() {
    const text = inputEl?.value?.trim();
    if (!text || loading.get()) return;
    inputEl.value = '';
    inputEl.style.height = 'auto';
    removeErrorBubbles();
    sendUserMessage(text, false);
  }

  async function sendUserMessage(text, isRetry = false) {
    const sessionId = activeSessionId.get();
    if (!sessionId) return;

    // Track whether the user is still viewing this session
    const isStillActive = () => activeSessionId.get() === sessionId;

    loadingSessions.add(sessionId);
    refreshSidebar();

    const userTime = new Date().toISOString();
    if (!isRetry) {
      if (isStillActive()) {
        messages.set(prev => [...prev, { role: 'user', text, time: userTime }]);
        appendMessage('user', text, userTime);
      }
      addMessage(sessionId, 'user', text).catch(() => {});

      // Auto-title: set title from first user message
      const sess = await getSession(sessionId).catch(() => null);
      if (sess && !sess.title) {
        const title = text.length > 60 ? text.slice(0, 57) + '...' : text;
        await updateSession(sessionId, { title }).catch(() => {});
        refreshSidebar();
      }
    }

    loading.set(true);
    setInputEnabled(false);
    if (isStillActive()) showTyping();

    try {
      abortController = new AbortController();

      const options = {};
      const history = chatHistory.get();
      if (history.length > 0) options.chatHistory = history;

      const startResult = await startInteraction(sessionId, text, options);
      const { interactionId } = startResult;

      let streamingStarted = false;
      let updateBubble = null;
      let streamingWrapper = null;

      if (isStillActive()) {
        updateBubble = appendStreamingBubble();
        streamingWrapper = messagesEl.lastElementChild;
        streamingWrapper.style.display = 'none';
      }

      const result = await pollInteraction(sessionId, interactionId, abortController.signal, partialText => {
        if (!isStillActive()) return;
        if (!streamingStarted) {
          streamingStarted = true;
          hideTyping();
          if (streamingWrapper) streamingWrapper.style.display = '';
        }
        if (updateBubble) updateBubble(partialText);
      });

      const {
        replyText,
        chatHistory: newHistory,
        isContentFiltered,
      } = handlePollResult(result, startResult.chatHistory);

      if (isStillActive()) {
        if (!streamingStarted) {
          hideTyping();
          if (streamingWrapper) streamingWrapper.style.display = '';
        }

        if (isContentFiltered) {
          if (streamingWrapper) streamingWrapper.remove();
          const userBubbles = messagesEl?.querySelectorAll('.gaia-msg--user');
          if (userBubbles?.length) userBubbles[userBubbles.length - 1].remove();
          const blockedText = text;
          messages.set(prev => {
            const lastUserIdx = prev.findLastIndex(m => m.role === 'user');
            if (lastUserIdx >= 0) return [...prev.slice(0, lastUserIdx), ...prev.slice(lastUserIdx + 1)];
            return prev;
          });
          messages.set(prev => [...prev, { role: 'error', text: replyText }]);
          appendContentFilteredError(replyText, blockedText);
        } else {
          if (updateBubble) updateBubble(replyText, true);
          const aiTime = new Date().toISOString();
          messages.set(prev => [...prev, { role: 'ai', text: replyText, time: aiTime }]);
        }
      }

      // Always persist to IndexedDB regardless of active session
      if (!isContentFiltered) {
        addMessage(sessionId, 'ai', replyText).catch(() => {});
      }
      if (newHistory) {
        if (isStillActive()) chatHistory.set(newHistory);
        await updateSession(sessionId, { gaia_chat_history: newHistory }).catch(() => {});
      }
      await updateSession(sessionId).catch(() => {});
      if (!isStillActive()) unreadSessions.add(sessionId);
      refreshSidebar();
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('rAI chat error:', err);

      if (err.response?.status === 404) {
        // Session expired — clean up IndexedDB and reset
        await deleteSessionById(sessionId).catch(() => {});
        if (isStillActive()) {
          activeSessionId.set(null);
          chatHistory.set([]);
          messages.set([]);
          appendErrorBubble('Session expired. Please start a new conversation.', false);
        }
        refreshSidebar();
      } else if (isStillActive()) {
        const streamBubble = messagesEl?.querySelector('.gaia-msg--ai:last-child');
        if (streamBubble && !streamBubble.classList.contains('gaia-msg--error')) streamBubble.remove();
        lastFailedText = text;
        const errorText = 'Something went wrong \u2014 click retry to resend.';
        messages.set(prev => [...prev, { role: 'error', text: errorText }]);
        appendErrorBubble(errorText);
      }
    } finally {
      loadingSessions.delete(sessionId);
      loading.set(false);
      setInputEnabled(true);
      if (isStillActive()) {
        hideTyping();
        inputEl?.focus();
      }
      refreshSidebar();
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  // ---- Session management ----

  async function handleNewSession() {
    // Show loading spinner while waiting for GAIA
    hideMainEmpty();
    clearChatArea();
    const spinnerOverlay = (
      <div class="rai-session-loading">
        <span class="spinner-border text-primary" role="status" aria-hidden="true" />
        <p class="rai-session-loading__text">Setting up your conversation…</p>
      </div>
    );
    const chatBody = mainEl.querySelector('.rai-main__chat');
    chatBody.style.display = '';
    messagesEl.appendChild(spinnerOverlay);

    try {
      const res = await createSession();
      const sessionId = res.sessionId;
      await createChatSession(sessionId);
      activeSessionId.set(sessionId);
      messages.set([]);
      chatHistory.set([]);
      lastFailedText = null;
      clearChatArea();
      refreshSidebar();
      inputEl?.focus();
    } catch (err) {
      console.error('Failed to create session:', err);
      clearChatArea();
      appendErrorBubble('Failed to create a new session. Please try again.', false);
    }
  }

  async function loadSession(sessionId) {
    // Reset UI state if switching away from a loading session
    if (loading.get()) {
      hideTyping();
      setInputEnabled(true);
    }

    activeSessionId.set(sessionId);
    lastFailedText = null;
    unreadSessions.delete(sessionId);
    hideMainEmpty();

    // Load messages from IndexedDB
    const msgs = await getSessionMessages(sessionId).catch(() => []);
    messages.set(msgs);

    // Load chatHistory from session record
    const sess = await getSession(sessionId).catch(() => null);
    chatHistory.set(sess?.gaia_chat_history || []);

    // Render messages
    clearChatArea();
    if (msgs.length === 0) {
      showEmptyState();
    } else {
      hideEmptyState();
      for (const m of msgs) {
        if (m.role === 'error') {
          appendErrorBubble(m.text, false);
        } else {
          const bubble = <MessageBubble role={m.role} text={m.text} time={m.time} />;
          messagesEl.appendChild(bubble);
        }
      }
      scrollToBottom();
    }

    refreshSidebar();
    inputEl?.focus();
  }

  async function handleDeleteSession(sessionId) {
    // Delete from GAIA backend (best-effort)
    deleteSession(sessionId).catch(() => {});
    // Delete from IndexedDB
    await deleteSessionById(sessionId).catch(() => {});

    if (activeSessionId.get() === sessionId) {
      activeSessionId.set(null);
      messages.set([]);
      chatHistory.set([]);
      clearChatArea();
      showMainEmpty();
    }
    refreshSidebar();
  }

  function clearChatArea() {
    if (!messagesEl) return;
    messagesEl.innerHTML = '';
    messagesEl.appendChild(
      <div class="gaia-empty-state">
        <i class="fas fa-robot gaia-empty-state__icon" />
        <p class="gaia-empty-state__text">Hey there! I'm rAI — ask me anything.</p>
      </div>,
    );
    hideTyping();
  }

  // ---- Sidebar rendering ----

  async function refreshSidebar() {
    const allSessions = await getAllSessions().catch(() => []);
    sessions.set(allSessions);
    renderSidebar(allSessions);
  }

  function renderSidebar(allSessions) {
    if (!sidebarListEl) return;
    sidebarListEl.innerHTML = '';

    if (allSessions.length === 0) {
      sidebarListEl.appendChild(
        <div class="rai-sidebar__empty">
          <p>No conversations yet.</p>
          <p>Click "New Chat" to start.</p>
        </div>,
      );
      return;
    }

    const groups = groupSessionsByDate(allSessions);
    const currentId = activeSessionId.get();

    for (const group of groups) {
      sidebarListEl.appendChild(<div class="rai-session-group__label">{group.label}</div>);

      for (const sess of group.sessions) {
        const isActive = sess.session_id === currentId;
        const title = sess.title || 'New conversation';
        const d = new Date(sess.last_activity);
        const now = new Date();
        const isToday = d.toDateString() === now.toDateString();
        const yesterday = new Date(now);
        yesterday.setDate(now.getDate() - 1);
        const isYesterday = d.toDateString() === yesterday.toDateString();
        const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        const weekday = d.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' });
        const timeStr = isToday ? time : isYesterday ? `Yesterday, ${time}` : `${weekday}, ${time}`;
        const expiresAt = new Date(d.getTime() + 14 * 24 * 60 * 60 * 1000);
        const hoursLeft = Math.max(0, Math.floor((expiresAt - now) / (1000 * 60 * 60)));
        const isExpiring = hoursLeft <= 48;

        const isLoading = loadingSessions.has(sess.session_id);
        const isUnread = unreadSessions.has(sess.session_id);

        const deleteBtn = (
          <button
            class="rai-session-item__delete"
            title="Delete conversation"
            onclick={e => {
              e.stopPropagation();
              handleDeleteSession(sess.session_id);
            }}>
            <i class="fas fa-trash-alt" />
          </button>
        );

        const typingDots = isLoading ? (
          <span class="rai-session-item__typing">
            <span />
            <span />
            <span />
          </span>
        ) : null;

        const item = (
          <div
            class={`rai-session-item ${isActive ? 'rai-session-item--active' : ''}`}
            onclick={() => loadSession(sess.session_id)}>
            <div class="rai-session-item__content">
              <span class="rai-session-item__title">
                {isUnread && !isActive ? <span class="rai-session-item__unread" /> : null}
                {title}
                {typingDots}
              </span>
              <span class="rai-session-item__time">{timeStr}</span>
              {isExpiring && (
                <span class="rai-session-item__expiring">Expires in {hoursLeft > 0 ? `${hoursLeft}h` : '<1h'}</span>
              )}
            </div>
            {deleteBtn}
          </div>
        );

        sidebarListEl.appendChild(item);
      }
    }
  }

  // ---- Build the DOM tree ----

  const sidebarEl = (
    <div class="rai-sidebar">
      <div class="rai-sidebar__header">
        <button class="rai-sidebar__new-btn" onclick={handleNewSession}>
          <i class="fas fa-plus" />
          <span>New Chat</span>
        </button>
      </div>
      <div class="rai-sidebar__list" />
    </div>
  );
  sidebarListEl = sidebarEl.querySelector('.rai-sidebar__list');

  const mainEl = (
    <div class="rai-main">
      {/* Header */}
      <div class="rai-main__header gaia-panel__header">
        <div class="gaia-header__left">
          <img src={bmwLogoSrc} class="gaia-header__bmw-logo" alt="BMW" />
          <i class="fas fa-robot gaia-header__icon" />
          <span class="gaia-header__title">Chat with rAI</span>
        </div>
        <div class="gaia-header__actions">
          <button
            class="gaia-header__btn gaia-header__info-btn"
            data-tooltip="About rAI"
            onclick={e => {
              e.stopPropagation();
              mainEl.querySelector('.gaia-info-box').classList.toggle('gaia-info-box--visible');
            }}>
            <i class="fas fa-info-circle" />
          </button>
          <button
            class="gaia-header__btn rai-header__delete-session"
            data-tooltip="Delete conversation"
            disabled
            onclick={() => {
              const sid = activeSessionId.get();
              if (sid) handleDeleteSession(sid);
            }}>
            <i class="fas fa-trash-alt" />
          </button>
        </div>
        <div class="gaia-info-box">
          <p class="gaia-info-box__title">About rAI</p>
          <p class="gaia-info-box__text">
            rAI is powered by <strong>GAIA</strong> — BMW Group's Generative AI platform. Learn more at{' '}
            <a href="https://gaia.bmwgroup.net/genai/" target="_blank" rel="noopener noreferrer">
              gaia.bmwgroup.net
            </a>
            .
          </p>
          <p class="gaia-info-box__disclaimer">
            <i class="fas fa-exclamation-triangle" /> <strong>Disclaimer:</strong> Messages generated by AI can be
            inaccurate. Always verify critical information with the original documents.
          </p>
          <p class="gaia-info-box__text" style="margin-top: 0.75rem">
            <i class="fas fa-clock" /> Chat sessions are automatically deleted after <strong>14 days</strong> without
            interaction.
          </p>
        </div>
      </div>

      {/* Empty main state (no session selected) */}
      <div class="rai-main__empty">
        <i class="fas fa-robot rai-main__empty-icon" />
        <p class="rai-main__empty-text">Select a conversation or start a new one.</p>
        <button class="rai-main__empty-btn" onclick={handleNewSession}>
          <i class="fas fa-plus" />
          <span>New Chat</span>
        </button>
      </div>

      {/* Chat body (hidden until a session is selected) */}
      <div class="rai-main__chat gaia-body" style="display:none">
        <div class="gaia-messages">
          <div class="gaia-empty-state">
            <i class="fas fa-robot gaia-empty-state__icon" />
            <p class="gaia-empty-state__text">Hey there! I'm rAI — ask me anything.</p>
          </div>
        </div>
        <button class="gaia-scroll-pill" data-tooltip="Jump to latest">
          <i class="fas fa-angles-down" />
        </button>
        <TypingIndicator />
        <div class="gaia-input-bar">
          <textarea class="gaia-input" placeholder="Ask rAI something…" rows="1" onkeydown={handleKeyDown} />
          <button class="gaia-send-btn" data-tooltip="Send" onclick={handleSend}>
            <i class="fas fa-paper-plane" />
          </button>
        </div>
      </div>
    </div>
  );

  // ---- Grab refs ----
  emptyMainEl = mainEl.querySelector('.rai-main__empty');
  const chatBodyEl = mainEl.querySelector('.rai-main__chat');
  messagesEl = mainEl.querySelector('.gaia-messages');
  inputEl = mainEl.querySelector('.gaia-input');
  typingEl = mainEl.querySelector('.gaia-typing');
  sendBtnEl = mainEl.querySelector('.gaia-send-btn');
  scrollPillEl = mainEl.querySelector('.gaia-scroll-pill');

  // Wire up shared chat helpers
  chatRefs.messagesEl = messagesEl;
  chatRefs.scrollPillEl = scrollPillEl;
  chatRefs.typingEl = typingEl;
  chatRefs.inputEl = inputEl;
  chatRefs.sendBtnEl = sendBtnEl;

  const deleteBtnEl = mainEl.querySelector('.rai-header__delete-session');
  const infoBox = mainEl.querySelector('.gaia-info-box');
  const infoBtn = mainEl.querySelector('.gaia-header__info-btn');

  if (scrollPillEl) scrollPillEl.addEventListener('click', () => scrollToBottom());
  if (messagesEl) messagesEl.addEventListener('scroll', () => updateScrollPill());

  // Click-away to close info box
  document.addEventListener('click', e => {
    if (!infoBox.classList.contains('gaia-info-box--visible')) return;
    if (infoBox.contains(e.target) || infoBtn.contains(e.target)) return;
    infoBox.classList.remove('gaia-info-box--visible');
  });
  hideTyping();

  // Auto-resize textarea
  if (inputEl) {
    inputEl.addEventListener('input', () => {
      inputEl.style.height = 'auto';
      inputEl.style.height = Math.min(inputEl.scrollHeight, 120) + 'px';
    });
  }

  // Show/hide chat body when a session is active
  activeSessionId.subscribe(id => {
    if (deleteBtnEl) deleteBtnEl.disabled = !id;
    if (id) {
      chatBodyEl.style.display = '';
      emptyMainEl.style.display = 'none';
    } else {
      chatBodyEl.style.display = 'none';
      emptyMainEl.style.display = '';
    }
  });

  // ---- Initialize: load sessions from IndexedDB ----
  openDB()
    .then(async () => {
      const all = await getAllSessions();
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - 14);
      const stale = all.filter(s => new Date(s.last_activity) < cutoff);
      await Promise.all(
        stale.map(s =>
          Promise.all([deleteSession(s.session_id).catch(() => {}), deleteSessionById(s.session_id).catch(() => {})]),
        ),
      );
    })
    .then(() => refreshSidebar())
    .then(async () => {
      const allSessions = sessions.get();
      if (allSessions.length > 0) {
        await loadSession(allSessions[0].session_id);
      }
    })
    .catch(err => console.error('Failed to initialize rAI chat DB:', err));

  const page = <div class="rai-page__layout" />;
  page.appendChild(sidebarEl);
  page.appendChild(mainEl);
  return page;
}
